import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { i18n } from '../i18n/nl';
import type { ChannelId } from '../state/types';

/**
 * `ChannelId` woont in state/types.ts (de gekozen-kanaal-state hoort
 * bij de root-state). Hier alleen re-exporteerd voor lokaal gebruik.
 */
export type { ChannelId };

export type ChannelAvailability = {
  webchatOnline: boolean;
};

export type ChannelMeta = {
  id: ChannelId;
  name: string;
  blurb: string;
  /** Alleen webchat: alternatieve blurb wanneer service offline staat. */
  blurbOffline?: string;
  /** SLA-tekst, bv. "Antwoord binnen 2 uur". */
  meta: string;
  /** Hoofdkleur (CTA-knop, status-dot). Hex of CSS-kleur-string. */
  color: string;
  /** Achtergrond van het ronde logo-vakje. */
  bg: string;
  /**
   * URL naar een logo-image (PNG/SVG). Wanneer aanwezig, wordt de
   * built-in SVG vervangen door `<img src>`. Bewust géén inline-SVG
   * via `unsafeHTML` om XSS-risico van host-supplied markup te
   * voorkomen.
   */
  logoUrl?: string;
};

/**
 * Per-veld optioneel; alle velden vallen terug op de built-in defaults.
 * Host-pages kunnen via `window.chatConfig.channelOverrides` per kanaal
 * bv. de SLA-tekst, de kleuren, of het logo overschrijven.
 */
export type ChannelOverride = Partial<Omit<ChannelMeta, 'id'>>;
export type ChannelOverrides = Partial<Record<ChannelId, ChannelOverride>>;

/**
 * Welke kanalen tellen als "chat met medewerker" (worden geclusterd
 * onder die label wanneer er ook diensten-kanalen actief zijn) versus
 * "dienst" (eigen rij in de picker, geen drilldown).
 */
export const CHAT_CHANNEL_IDS: readonly ChannelId[] = ['webchat', 'whatsapp', 'messenger'] as const;
export const SERVICE_CHANNEL_IDS: readonly ChannelId[] = ['repair-request'] as const;

export function splitChannels(channels: readonly ChannelId[]): {
  chats: ChannelId[];
  services: ChannelId[];
} {
  return {
    chats: channels.filter((c) => CHAT_CHANNEL_IDS.includes(c)),
    services: channels.filter((c) => SERVICE_CHANNEL_IDS.includes(c)),
  };
}

/**
 * Of de picker een gegroepeerde weergave moet renderen: "Chatten met
 * medewerker" als één rij die drilldown't naar een chat-list, plus elk
 * service-kanaal als eigen rij. Triggert alleen bij meerdere chats én
 * minstens één dienst — single chat-channel + dienst rendert flat.
 */
export function shouldGroupChannels(channels: readonly ChannelId[]): boolean {
  const { chats, services } = splitChannels(channels);
  return chats.length > 1 && services.length > 0;
}

/**
 * Per-kanaal config = i18n-tekst (naam, blurb, blurbOffline, meta) +
 * visuele config (kleur, achtergrond). De tekst woont in `i18n.channels`
 * (nl.ts) zodat alle copy centraal staat en vertaalbaar is; alleen de
 * visuele velden + `id` staan hier.
 */
export const DEFAULT_CHANNELS: Record<ChannelId, ChannelMeta> = {
  webchat: {
    id: 'webchat',
    ...i18n.channels.webchat,
    color: '#0C1C47',
    bg: '#EBF0FC',
  },
  'repair-request': {
    id: 'repair-request',
    ...i18n.channels['repair-request'],
    color: '#B45309', // warm amber — onderscheid van de blauwe webchat
    bg: '#FEF3C7',
  },
  whatsapp: {
    id: 'whatsapp',
    ...i18n.channels.whatsapp,
    color: '#25D366',
    bg: '#E7F9EE',
  },
  messenger: {
    id: 'messenger',
    ...i18n.channels.messenger,
    color: '#0084FF',
    bg: '#E5F2FF',
  },
};

/**
 * Defaults gemerged met host-supplied overrides. Onbekende channel-id's
 * in `overrides` zijn type-system-onmogelijk (Partial<Record<ChannelId>>);
 * onbekende velden worden door de spread-operator simpelweg gemerged en
 * door de component genegeerd. `id` is per definitie niet override-baar.
 */
export function resolveChannelMeta(
  id: ChannelId,
  overrides?: ChannelOverrides,
): ChannelMeta {
  const override = overrides?.[id] ?? {};
  return { ...DEFAULT_CHANNELS[id], ...override };
}

/** @deprecated Gebruik `DEFAULT_CHANNELS` of `resolveChannelMeta()`. */
export const CHANNELS = DEFAULT_CHANNELS;

/**
 * <embrace-channel-picker> — keuze tussen kanalen (webchat / WhatsApp / Messenger).
 * Wordt alleen getoond als er meer dan 1 kanaal geconfigureerd is in host-config.
 */
@customElement('embrace-channel-picker')
export class EmbraceChannelPicker extends LitElement {
  @property({ attribute: false })
  channels: ChannelId[] = ['webchat'];

  @property({ type: Boolean })
  webchatOnline = true;

  /**
   * Optionele per-kanaal overrides voor naam, blurb, kleuren, SLA, en/of
   * logo-URL. Velden die niet gezet zijn vallen terug op `DEFAULT_CHANNELS`.
   */
  @property({ attribute: false })
  channelOverrides?: ChannelOverrides;

  /**
   * Sub-state van de picker bij gegroepeerde weergave. `'root'` toont
   * "Chatten met medewerker" + dienst-kanalen; `'chat-list'` toont de
   * individuele chat-kanalen. Wordt genegeerd zonder grouping.
   */
  @property()
  step: 'root' | 'chat-list' = 'root';

  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    .body {
      flex: 1;
      padding: 26px 14px 16px;
      overflow-y: auto;
      background: var(--ec-background);
    }

    .list {
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 4px 2px;
    }

    .row {
      position: relative;
      display: flex;
      align-items: center;
      gap: 14px;
      background: #fff;
      border: 1px solid var(--ec-border);
      border-radius: 16px;
      padding: 16px 18px;
      cursor: pointer;
      text-align: left;
      transition: border-color var(--ec-duration-fast) var(--ec-ease-smooth),
                  box-shadow var(--ec-duration-fast) var(--ec-ease-smooth),
                  transform .1s var(--ec-ease-smooth);
      font-family: var(--ec-font-family);
      color: var(--ec-text);
      box-shadow: 0 2px 6px -2px rgba(12,28,71,.08), 0 8px 20px -12px rgba(12,28,71,.12);
    }
    .row:hover {
      border-color: rgba(12,28,71,.22);
      box-shadow: 0 4px 10px -2px rgba(12,28,71,.12), 0 14px 28px -14px rgba(12,28,71,.22);
    }
    .row:active { transform: scale(.995); }
    .row:focus-visible {
      outline: var(--ec-focus-ring-width) solid var(--ec-focus-ring);
      outline-offset: var(--ec-focus-ring-offset);
    }

    .logo {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
    }

    .info {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .name {
      font: 700 15px/1.3 var(--ec-font-display);
      color: var(--ec-text);
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }

    .status {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font: 600 11px/1 var(--ec-font-family);
      text-transform: lowercase;
      padding: 3px 8px 3px 7px;
      border-radius: 9999px;
    }
    .status .dot {
      width: 6px;
      height: 6px;
      border-radius: 9999px;
      background: currentColor;
    }
    .status.on { color: #0B7D5C; background: #E6F6EF; }
    .status.off { color: #8C4A00; background: #FDF0D9; }

    .blurb {
      font: 400 13px/1.4 var(--ec-font-family);
      color: #5E5F62;
    }

    .chev {
      color: #8C8D8F;
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform var(--ec-duration-fast), color var(--ec-duration-fast);
    }
    .row:hover .chev {
      color: var(--ec-text);
      transform: translateX(2px);
    }
  `;

  override render() {
    const grouped = shouldGroupChannels(this.channels);
    const { chats, services } = splitChannels(this.channels);

    const rows = !grouped
      ? this.channels.map((id) => this.renderRow(id))
      : this.step === 'chat-list'
      ? chats.map((id) => this.renderRow(id))
      : [this.renderChatGroupRow(), ...services.map((id) => this.renderRow(id))];

    return html`
      <div class="body">
        <div class="list">${rows}</div>
      </div>
    `;
  }

  /**
   * Synthetic rij "Chatten met medewerker" die bij click drilldown't naar
   * de chat-list step. Gebruikt webchat-iconografie (chatbubble) als
   * herkenbare visuele cue.
   */
  private renderChatGroupRow() {
    return html`
      <button
        class="row"
        @click=${this.drilldownChat}
      >
        <span class="logo" style="background: #EBF0FC;">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0C1C47" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
          </svg>
        </span>
        <span class="info">
          <span class="name">${i18n.picker.chatGroup}</span>
          <span class="blurb">${i18n.picker.chatGroupBlurb}</span>
        </span>
        <span class="chev" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="m9 18 6-6-6-6"/>
          </svg>
        </span>
      </button>
    `;
  }

  private drilldownChat = (): void => {
    this.dispatchEvent(
      new CustomEvent('drilldown-chat', { bubbles: true, composed: true }),
    );
  };

  private renderStatusPill(online: boolean) {
    return html`
      <span class=${`status ${online ? 'on' : 'off'}`}>
        <span class="dot"></span>
        ${online ? i18n.picker.statusOnline : i18n.picker.statusOffline}
      </span>
    `;
  }

  private renderRow(id: ChannelId) {
    const meta = resolveChannelMeta(id, this.channelOverrides);
    const isWebchat = id === 'webchat';
    const unavailable = isWebchat && !this.webchatOnline;
    const blurb = unavailable ? meta.blurbOffline ?? meta.blurb : meta.blurb;
    // Webchat-status volgt de chat-service. Repair-request loopt op
    // Customers + AAVA APIs (los van de chat-service) — altijd online.
    // Externe kanalen (WhatsApp/Messenger) hebben geen status-pill.
    const onlineState =
      id === 'webchat'
        ? this.webchatOnline
        : id === 'repair-request'
        ? true
        : null;

    return html`
      <button
        class="row"
        @click=${() => this.pick(id)}
      >
        <span class="logo" style=${`background: ${meta.bg};`}>
          ${this.renderLogo(id, meta)}
        </span>
        <span class="info">
          <span class="name">
            ${meta.name}
            ${onlineState === null ? nothing : this.renderStatusPill(onlineState)}
          </span>
          <span class="blurb">${blurb}</span>
        </span>
        <span class="chev" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="m9 18 6-6-6-6"/>
          </svg>
        </span>
      </button>
    `;
  }

  private renderLogo(id: ChannelId, meta: ChannelMeta) {
    if (meta.logoUrl) {
      return html`<img class="logo-img" src=${meta.logoUrl} alt="" width="28" height="28" />`;
    }
    if (id === 'whatsapp') {
      return html`
        <svg width="28" height="28" viewBox="0 0 512 512" aria-hidden="true">
          <defs>
            <linearGradient id="wa-g" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color="#60D66A"/>
              <stop offset="1" stop-color="#25CF43"/>
            </linearGradient>
          </defs>
          <path fill="url(#wa-g)" d="M256 0C114.6 0 0 114.6 0 256c0 45 11.6 87.3 32 124L0 512l135-32a255 255 0 0 0 121 31c141.4 0 256-114.6 256-256S397.4 0 256 0Z"/>
          <path fill="#fff" fill-rule="evenodd" d="M256 464c-36 0-69.6-9.9-98.4-27l-68.7 18 18.3-67.2A207.1 207.1 0 0 1 48 256c0-114.9 93.1-208 208-208s208 93.1 208 208-93.1 208-208 208Zm119.3-157c-6.5-3.3-38.5-19-44.5-21.2-6-2.2-10.3-3.3-14.7 3.3-4.3 6.5-16.8 21.2-20.6 25.5-3.8 4.3-7.6 4.9-14 1.6-6.5-3.3-27.5-10.2-52.4-32.4-19.4-17.3-32.4-38.6-36.2-45.1-3.8-6.5-.4-10 2.9-13.3 3-2.9 6.5-7.6 9.8-11.4 3.3-3.8 4.3-6.5 6.5-10.9 2.2-4.3 1.1-8.2-.5-11.4-1.6-3.3-14.7-35.4-20.1-48.4-5.3-12.7-10.7-11-14.7-11.2-3.8-.2-8.1-.2-12.5-.2s-11.4 1.6-17.4 8.2c-6 6.5-22.8 22.3-22.8 54.4s23.4 63.1 26.6 67.4c3.3 4.3 46 70.3 111.5 98.6 15.6 6.7 27.7 10.7 37.2 13.7 15.6 5 29.8 4.3 41 2.6 12.5-1.9 38.5-15.7 43.9-30.9 5.4-15.2 5.4-28.3 3.8-30.9-1.6-2.7-6-4.3-12.5-7.6Z" clip-rule="evenodd"/>
        </svg>
      `;
    }
    if (id === 'repair-request') {
      // Sleutel-icoon — visueel onderscheidend van de chatbubble (webchat)
      // en de externe-platform-logo's. Amber-toon erfeert van meta.color.
      return html`
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke=${meta.color} stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
        </svg>
      `;
    }
    if (id === 'messenger') {
      return html`
        <svg width="28" height="28" viewBox="0 0 512 512" aria-hidden="true">
          <defs>
            <radialGradient id="msg-rg" cx="19%" cy="99%" r="108%">
              <stop offset="0" stop-color="#0099FF"/>
              <stop offset=".6" stop-color="#A033FF"/>
              <stop offset=".9" stop-color="#FF5280"/>
              <stop offset="1" stop-color="#FF7061"/>
            </radialGradient>
          </defs>
          <path fill="url(#msg-rg)" d="M256 0C112.6 0 0 105.1 0 246.8c0 74.1 30.7 138.3 80.7 182.5 4.2 3.8 6.7 9 6.9 14.6l1.4 45.7c.4 14.6 15.5 24 28.8 18.1l51-22.5c4.3-1.9 9.2-2.3 13.8-1 23.4 6.4 48.3 9.8 74.4 9.8 143.4 0 256-105.1 256-246.8S399.4 0 256 0Z"/>
          <path fill="#fff" d="m102.3 320.9 75.1-119.2c12-19 37.5-23.7 55.4-10.3l59.7 44.8a15 15 0 0 0 18.1-.1l80.7-61.2c10.8-8.2 24.8 4.7 17.6 16.2l-75.1 119.1c-12 19-37.5 23.8-55.4 10.3l-59.7-44.8a15 15 0 0 0-18.1.1l-80.7 61.2c-10.8 8.2-24.8-4.7-17.6-16.1Z"/>
        </svg>
      `;
    }
    // webchat — navy speech bubble with ellipsis
    return html`
      <svg width="28" height="28" viewBox="0 0 512 512" aria-hidden="true">
        <defs>
          <linearGradient id="wc-g" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stop-color="#22356A"/>
            <stop offset="1" stop-color="#0C1C47"/>
          </linearGradient>
        </defs>
        <path fill="url(#wc-g)" d="M256 64C132.3 64 32 148.8 32 253.4c0 58.7 31.8 111.2 81.5 146.1l-13.7 67.6c-2 9.7 8 17.3 16.8 12.7l82.3-43.2c18.6 4.6 38.2 7 58.2 7 123.7 0 224-84.8 224-189.4S379.7 64 256 64Z"/>
        <circle cx="168" cy="254" r="22" fill="#fff"/>
        <circle cx="256" cy="254" r="22" fill="#fff"/>
        <circle cx="344" cy="254" r="22" fill="#fff"/>
      </svg>
    `;
  }

  private pick(id: ChannelId) {
    this.dispatchEvent(
      new CustomEvent('pick-channel', {
        detail: { channel: id },
        bubbles: true,
        composed: true,
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'embrace-channel-picker': EmbraceChannelPicker;
  }
}
